import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";

const CircularProgress = ({ value, total, isDeficit }) => {
  const percentage = isDeficit ? 100 : Math.min(100, (value / total) * 100);
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (percentage / 100) * circumference;

  const progressColor = isDeficit ? "#EF4444" : "#22C55E";
  const textColor = isDeficit ? "text-red-500" : "text-green-500";
  const label = isDeficit ? "Deficit" : "Surplus";
  const textSizeClass =
    value.toString().length <= 4
      ? "text-xl sm:text-3xl"
      : value.toString().length <= 6
      ? "text-lg sm:text-2xl"
      : "text-sm sm:text-lg";

  return (
    <div className="relative w-20 h-20 sm:w-24 sm:h-24">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="white"
          className="stroke-[#e32ca6]"
          strokeWidth="4"
        />
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          strokeWidth="4"
          stroke={progressColor}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span  className={`font-bold ${textSizeClass} ${textColor}`}>
          {value}
        </span>
        <span className="text-xs sm:text-sm">{label}</span>
      </div>
    </div>
  );
};

const AnimatedValue = ({ value, className }) => {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  return (
    <span className={`transition-all duration-300 ease-in-out ${className}`}>
      {displayValue}
    </span>
  );
};


const MealCard = ({ meal, data, isExpanded, onToggle,plateCount,scannedCount }) => {
  const isDeficit = data.deficit !== undefined;
  const surplusOrDeficit = isDeficit ? data.deficit : data.surplus;
  const bgColor = isExpanded ? "bg-[#1F0F4A]" : "bg-[#F0F1F6]";
  const textColor = isExpanded ? "text-white" : "text-[#1F0F4A]";

  const surplusColor = isDeficit
    ? "text-red-500"
    : meal === "breakfast" || meal === "dinner"
      ? "text-green-500"
      : meal === "lunch" && isExpanded
        ? "text-yellow-500"
        : "text-yellow-500";

  const isValidMeal = typeof data.time === "string" && data.time.trim() !== "";

  return (
    <div
      className={`rounded-3xl p-3 sm:p-4 mx-2 sm:mx-4 mb-2 sm:mb-4 ${bgColor} transition-all duration-700 ease-in-out`}
      style={{
        maxHeight: isExpanded ? "24rem" : "6rem",
        overflow: "hidden"
      }}
    >
      <div className="flex justify-between items-center font-semibold">
        {isExpanded ? (
          <>
            {!isValidMeal && (
              <div className="w-full flex justify-center">
                <div className="bg-white text-[#1F0F4A] p-1 sm:p-2 rounded-full flex items-center justify-center space-x-2">
                  <span className="font-jakarta font-semibold text-sm sm:text-base">
                    {meal}
                  </span>
                </div>
              </div>
            )}
            {isValidMeal && (
              <div className="w-full flex justify-center">
                <div className="bg-white text-[#1F0F4A] p-1 sm:p-2 rounded-full flex items-center justify-center space-x-2">
                  <span className="font-jakarta font-semibold text-sm sm:text-base">
                    {meal}
                  </span>
                  <span className="text-pink-500 text-xs sm:text-sm font-semibold">
                    {data.time}
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <span
              className={`font-jakarta font-semibold text-lg sm:text-xl ${textColor}`}
            >
              {meal}
            </span>
            <div className="flex items-center space-x-2">
              <div className="text-center">
                <AnimatedValue
                  value={surplusOrDeficit}
                  className={`text-2xl sm:text-3xl font-jakarta font-semibold ${surplusColor}`}
                />
                <div className={`text-xs ${surplusColor}`}>
                  {isDeficit ? "Deficit" : "Surplus"}
                </div>
              </div>
              <div className="text-center">
                <AnimatedValue
                  value={data.totalSales}
                  className={`text-lg sm:text-2xl font-jakarta ${textColor}`}
                />
                <div className={`text-xs ${textColor}`}>Total Sales</div>
              </div>
              <div className="text-center">
                <AnimatedValue
                  value={data.totalPlanned}
                  className={`text-lg sm:text-2xl font-jakarta ${textColor}`}
                />
                <div className={`text-xs ${textColor}`}>Total Planned</div>
              </div>
              <div className="cursor-pointer" onClick={onToggle}>
                <Plus
                  className={`w-5 h-5 sm:w-6 sm:h-6 text-white border rounded-3xl bg-[#E32CA6] transition-transform duration-500 ${isExpanded ? "rotate-45" : ""
                    }`}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <div
        className={`mt-2 sm:mt-4 transition-all duration-300 ease-in-out ${isExpanded ? "opacity-100" : "opacity-0"
          }`}
        style={{
          maxHeight: isExpanded ? "300px" : "0",
          overflow: "hidden"
        }}
      >
        <div className="flex justify-between items-center border-b border-white">
          <div className="text-center relative">
            <CircularProgress
              value={surplusOrDeficit}
              total={data.totalPlanned}
              isDeficit={isDeficit}
            />
          </div>
          <div className="text-center">
            <AnimatedValue
              value={data.totalSales}
              className={`text-lg sm:text-2xl font-jakarta ${textColor}`}
            />
            <div className={`text-xs ${textColor}`}>
              Total <br /> Sales
            </div>
          </div>
          <div className="text-center">
            <AnimatedValue
              value={data.totalPlanned}
              className={`text-lg sm:text-2xl font-jakarta ${textColor}`}
            />
            <div className={`text-xs ${textColor}`}>
              Total <br /> Planned
            </div>
          </div>
          <div className="text-center">
            <AnimatedValue
              value={data.last30MinsSales}
              className={`text-lg sm:text-2xl font-jakarta ${textColor}`}
            />
            <div className={`text-xs ${textColor}`}>Last 30 Mins Sales</div>
          </div>
        </div>
        <div className="flex justify-center mt-1 gap-2 ">
        <div className="text-center invisible">
            <AnimatedValue
              value={plateCount}
              className={` text-lg sm:text-2xl font-jakarta ${textColor}`}
            />
            <div className={`text-xs ${textColor}`}>Total Plates</div>
        </div>
        <div className="text-center ">
            <AnimatedValue
              value={scannedCount}
              className={`text-lg sm:text-2xl font-jakarta ${textColor}`}
            />
            <div className={`text-xs ${textColor}`}>Total Scanned</div>
        </div>
          </div>
      </div>
    </div>
  );
};

export default MealCard;
